/**
 * The socket to the game server (docs/server-model.md): JSON text frames and
 * binary frames over one WebSocket, reconnecting with backoff. No game
 * knowledge here (serverSession.ts has that) and no DOM beyond WebSocket, so
 * it runs under Node too.
 */

/** `ws://host:port`, `wss://host`, `http(s)://host` or bare `host:port` -> the server's `/ws` endpoint. */
export function normalizeServerUrl(raw: string): string {
  let s = raw.trim();
  if (/^https?:\/\//i.test(s)) s = 'ws' + s.slice(4);
  else if (!/^wss?:\/\//i.test(s)) s = 'ws://' + s;
  const m = /^(wss?:\/\/[^/?#]+)([^?#]*)/i.exec(s);
  if (!m) return s;
  const path = m[2].replace(/\/+$/, '');
  return m[1] + (path === '' ? '/ws' : path);
}

const KEY_STORAGE = 'opencraft.server.key';

interface KeyStore { getItem(k: string): string | null; setItem(k: string, v: string): void }

/** The secret this browser logs in with (32 random bytes, hex), made once. The server's player id is its hash. */
export function loadOrCreateKey(store?: KeyStore): string {
  try {
    const st = store ?? localStorage;
    const had = st.getItem(KEY_STORAGE);
    if (had && /^[0-9a-f]{64}$/.test(had)) return had;
    const key = randomKey();
    st.setItem(KEY_STORAGE, key);
    return key;
  } catch {
    return randomKey(); // storage unavailable: a player for this page load only
  }
}

function randomKey(): string {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  let s = '';
  for (const v of b) s += v.toString(16).padStart(2, '0');
  return s;
}

export interface ConnectionHandlers {
  open(): void;
  text(data: string): void;
  binary(data: Uint8Array): void;
  /**
   * The socket closed (or never opened). `retryMs` is when the next attempt starts, -1 when there is none;
   * `reason` is the close frame's text ('' when the peer just vanished), e.g. "server restarting".
   */
  close(retryMs: number, reason: string): void;
}

interface SocketLike {
  binaryType: string;
  readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code?: number; reason?: string } | undefined) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: string | ArrayBufferLike | ArrayBufferView): void;
  close(): void;
}
export type SocketCtor = new (url: string) => SocketLike;

const BACKOFF_MIN_MS = 500;
const BACKOFF_MAX_MS = 8000;

export class Connection {
  private ws: SocketLike | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private backoff = BACKOFF_MIN_MS;
  private stopped = true;

  constructor(
    readonly url: string,
    private readonly handlers: ConnectionHandlers,
    private readonly ctor: SocketCtor = WebSocket as unknown as SocketCtor,
  ) {}

  get open(): boolean { return this.ws !== null && this.ws.readyState === 1; }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.connect();
  }

  /** Close for good: no more reconnects. */
  stop(): void {
    this.stopped = true;
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
    const ws = this.ws;
    this.ws = null;
    if (ws) { try { ws.close(); } catch { /* already closed */ } }
  }

  /** False when the socket is not open (the message is dropped: intents are not queued across a reconnect). */
  send(data: string | Uint8Array): boolean {
    if (!this.open) return false;
    try { this.ws!.send(data); return true; } catch { return false; }
  }

  private connect(): void {
    this.timer = null;
    let ws: SocketLike;
    try { ws = new this.ctor(this.url); } catch { this.retry(); return; }
    this.ws = ws;
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.backoff = BACKOFF_MIN_MS;
      this.handlers.open();
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      const d = ev.data;
      if (typeof d === 'string') this.handlers.text(d);
      else if (d instanceof ArrayBuffer) this.handlers.binary(new Uint8Array(d));
      else if (ArrayBuffer.isView(d)) this.handlers.binary(new Uint8Array(d.buffer, d.byteOffset, d.byteLength));
    };
    ws.onerror = () => { /* a close always follows */ };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.retry(typeof ev?.reason === 'string' ? ev.reason : '');
    };
  }

  private retry(reason = ''): void {
    if (this.stopped) { this.handlers.close(-1, reason); return; }
    // up to 25% longer, so the players of a restarted server do not all come back in the same instant
    const wait = Math.round(this.backoff * (1 + Math.random() * 0.25));
    this.backoff = Math.min(BACKOFF_MAX_MS, this.backoff * 2);
    this.timer = setTimeout(() => this.connect(), wait);
    this.handlers.close(wait, reason);
  }
}
